import { Component } from '@verndale/core';

declare global {
  interface Window {
    submitSignUpToNewsletter?: () => void;
  }
}

class SignUpToNewsletterForm extends Component {
  form: HTMLFormElement | null = null;
  successMessage: HTMLElement | null = null;
  errorMessage: HTMLElement | null = null;
  submitButton: HTMLButtonElement | null = null;
  recaptchaToken: HTMLInputElement | null = null;

  private isValidEmail(email: string): boolean {
    // Regular expression for email validation including domain
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  constructor(el: HTMLElement) {
    super(el);
    this.form = null;
    this.successMessage = null;
    this.errorMessage = null;
    this.submitButton = null;
    this.recaptchaToken = null;
  }

  setupDefaults() {
    this.dom = {
      form: this.el.querySelector('form') as HTMLFormElement,
      successMessage: this.el.querySelector(
        '.sign-up-to-news-letter__message--success'
      ) as HTMLElement,
      errorMessage: this.el.querySelector('.sign-up-to-news-letter__message--error') as HTMLElement,
      submitButton: this.el.querySelector('button[type="submit"]') as HTMLButtonElement,
      recaptchaToken: this.el.querySelector('input[name="reCaptchaToken"]') as HTMLInputElement
    };

    // Hide messages on initial load
    if (this.dom.successMessage) {
      (this.dom.successMessage as HTMLElement).classList.add('hidden');
    }
    if (this.dom.errorMessage) {
      (this.dom.errorMessage as HTMLElement).classList.add('hidden');
    }

    // Ensure form doesn't have a target attribute that could cause new window/tab
    if (this.dom.form && (this.dom.form as HTMLFormElement).hasAttribute('target')) {
      (this.dom.form as HTMLFormElement).removeAttribute('target');
    }
  }

  addListeners() {
    if (this.dom.form) {
      // Use direct function reference instead of bind to ensure proper "this" context
      (this.dom.form as HTMLFormElement).addEventListener('submit', this.handleSubmit.bind(this));

      // Add input validation for email field
      const emailInput = (this.dom.form as HTMLFormElement).querySelector(
        'input[type="email"]'
      ) as HTMLInputElement;
      if (emailInput) {
        emailInput.addEventListener('input', () => {
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[net|com]+$/;
          if (!emailRegex.test(emailInput.value)) {
            emailInput.setCustomValidity(
              'Please include a valid domain extension such as .com or .net'
            );
          } else {
            emailInput.setCustomValidity('');
          }
        });
      }

      // As a backup, also intercept the click on the submit button
      if (this.dom.submitButton) {
        (this.dom.submitButton as HTMLButtonElement).addEventListener('click', e => {
          if (this.dom.form && !(this.dom.form as HTMLFormElement).checkValidity()) {
            // Let the browser handle validation
            return;
          }
          e.preventDefault();
          this.handleSubmit(e);
        });
      }
    }
  }

  showSuccessMessage() {
    if (this.dom.successMessage) {
      (this.dom.successMessage as HTMLElement).classList.remove('hidden');
    }
    if (this.dom.errorMessage) {
      (this.dom.errorMessage as HTMLElement).classList.add('hidden');
    }
  }

  showErrorMessage(
    message: string = 'There was an error processing your request. Please try again.'
  ) {
    if (this.dom.errorMessage) {
      const errorParagraph = (this.dom.errorMessage as HTMLElement).querySelector('p');
      if (errorParagraph) {
        errorParagraph.textContent = message;
      }
      (this.dom.errorMessage as HTMLElement).classList.remove('hidden');
    }
    if (this.dom.successMessage) {
      (this.dom.successMessage as HTMLElement).classList.add('hidden');
    }
  }

  async handleSubmit(event: Event) {
    // Must call preventDefault immediately
    event.preventDefault();
    event.stopPropagation();

    // Hide any previous messages
    if (this.dom.successMessage) {
      (this.dom.successMessage as HTMLElement).classList.add('hidden');
    }
    if (this.dom.errorMessage) {
      (this.dom.errorMessage as HTMLElement).classList.add('hidden');
    }

    // Get the email input
    const emailInput = (this.dom.form as HTMLFormElement)?.querySelector(
      'input[type="email"]'
    ) as HTMLInputElement;
    if (emailInput && !this.isValidEmail(emailInput.value)) {
      this.showErrorMessage(
        'Please enter a valid email address with a proper domain (e.g., example@domain.com)'
      );
      return;
    }

    // Using native form validation
    if (this.dom.form && !(this.dom.form as HTMLFormElement).checkValidity()) {
      // Let the browser handle the validation UI
      (this.dom.form as HTMLFormElement).reportValidity();
      return;
    }

    if (this.dom.submitButton) {
      (this.dom.submitButton as HTMLButtonElement).disabled = true;
    }

    try {
      if (this.dom.form) {
        const formAction = (this.dom.form as HTMLFormElement).getAttribute('action') || '';
        const source = (this.dom.form as HTMLFormElement).getAttribute('data-source') || '';
        const reCaptchaKey =
          (this.dom.form as HTMLFormElement).getAttribute('data-recaptcha-key') || '';
        const reCaptchaToken = (this.dom.recaptchaToken as HTMLInputElement).value || '';
        // Create a data object like the contact-us form
        const formData = {} as Record<string, string>;
        const formElements = Array.from(
          (this.dom.form as HTMLFormElement).elements
        ) as HTMLFormElement[];

        formElements.forEach(element => {
          if ((element.name && 'value' in element) || !element.name.match('g-recaptcha-response')) {
            formData[element.name] = element.value;
          }
        });

        formData['source'] = source;
        if (this.dom.recaptchaToken) {
          formData['reCaptchaToken'] = reCaptchaToken || '';
        }
        formData['reCaptchaKey'] = reCaptchaKey;

       
        const response = await fetch(formAction, {
          method: 'POST',
          body: JSON.stringify(formData),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Server responded with ${response.status}`);
        }

         // Ensure the function is defined or imported to post customer data to Zaius
         if (typeof window.submitSignUpToNewsletter === 'function') {
          window.submitSignUpToNewsletter();
        } else {
          console.error('submitSignUpToNewsletter is not defined on the window object');
        }

        // Clear the form
        (this.dom.form as HTMLFormElement).reset();

        // Show success message
        this.showSuccessMessage();

        // Scroll to the success message
        (this.dom.successMessage as HTMLElement)?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      this.showErrorMessage();
    } finally {
      if (this.dom.submitButton) {
        (this.dom.submitButton as HTMLButtonElement).disabled = false;
      }
    }

    // Return false as an extra precaution to prevent form submission
    return;
  }
}

export default SignUpToNewsletterForm;
